import { createTheme } from "@mui/material";
import { fontStyles } from "./fontStyles";
import c from "lib/style/colorVar.scss";

const buttonLoadingSelector = [
  '&[data-loading="true"]',
  '&[data-loading="true"]:hover',
  '&[data-loading="true"]:active',
  '&[data-loading="true"].Mui-disabled',
  '&[data-loading="true"].Mui-disabled:hover',
  '&[data-loading="true"].Mui-disabled:active',
].join(", ");

const buttonTextStyle = {
  fontFamily: "Noto Sans, Roboto, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "100%",
  letterSpacing: "0.14px",
};

const buttonSpinnerStyle = {
  color: "inherit",
  fontSize: "inherit",
};

const buttonIconAndSpinnerSelector =
  "& .MuiButton-startIcon, & .MuiButton-endIcon, & .button-mas-spinner, & .button-mas-overlay-spinner";

const buttonIconAndSpinnerSize = (fontSize) => ({
  [buttonIconAndSpinnerSelector]: {
    fontSize,
  },
});

const buttonTransparentInteraction = (color, disabledColor) => ({
  color,
  backgroundColor: "transparent",
  "&.Mui-disabled": {
    color: disabledColor,
    backgroundColor: "transparent",
  },
  [buttonLoadingSelector]: {
    color,
    backgroundColor: "transparent",
  },
});

export const theme = createTheme({
  palette: {
    info: {
      main: c["brand1"],
      light: c["brandLight50"],
      dark: c["brandDark50"],
    },
    success: {
      main: c["success"],
      light: c["successLight"],
      // dark: c["successBg"],
    },
    warning: {
      main: c["warning"],
      light: c["warningLight"],
      dark: c["warningDark"],
    },
    error: {
      main: c["danger"],
      light: c["dangerLight"],
      dark: c["dangerDark"],
    }
  },
  typography: {
    fontFamily: "Noto Sans, Roboto, sans-serif",
    ...fontStyles,
  },
  components: {
    MuiTypography: {
      defaultProps: {
        fontFamily: "Noto Sans, Roboto, sans-serif",
        color: c["black"],
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: c["brand1"],
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          ...buttonTextStyle,
          minWidth: "auto",
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          flexShrink: 0,
          position: "relative",
          borderRadius: "4px",
          border: "1px solid transparent",
          textTransform: "none",
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
          userSelect: "none",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            margin: 0,
            fontSize: "inherit",
          },
          "& .MuiButton-startIcon > *, & .MuiButton-endIcon > *": {
            fontSize: "inherit",
          },
          "& .button-mas-spinner, & .button-mas-overlay-spinner": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "inherit",
            color: "inherit",
          },
          "& .button-mas-spinner .MuiCircularProgress-root, & .button-mas-overlay-spinner .MuiCircularProgress-root":
            buttonSpinnerStyle,
          "& .button-mas-label": {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.2s ease",
            color: "inherit",
            fontWeight: "inherit",
          },
          "& .button-mas-label *": {
            color: "inherit",
            fontWeight: "inherit",
          },
          "& .button-mas-overlay-spinner": {
            position: "absolute",
            inset: 0,
            opacity: 0,
            pointerEvents: "none",
          },
          '&[data-loading="true"]': {
            opacity: "1 !important",
            pointerEvents: "none",
          },
          '&[data-loading-overlay="true"] .button-mas-label': {
            opacity: 0,
          },
          '&[data-loading-overlay="true"] .button-mas-overlay-spinner': {
            opacity: 1,
          },
        },
      },
      variants: [
        ...["md", "medium"].map((size) => ({
          props: { size },
          style: {
            minHeight: "32px",
            padding: "4px 16px",
          },
        })),
        ...["sm", "small"].map((size) => ({
          props: { size },
          style: {
            minHeight: "24px",
            padding: "2px 8px",
            fontSize: "12px",
            ...buttonIconAndSpinnerSize("12px"),
          },
        })),
        {
          props: { size: "table-bar-button" },
          style: {
            minHeight: "32px",
            padding: "8px 12px",
          },
        },
        {
          props: { size: "dialog-button" },
          style: {
            minHeight: "40px",
            padding: "8px 32px",
            width: "auto",
            minWidth: 0,
            flex: "1 1 0",
            ...buttonIconAndSpinnerSize("20px"),
          },
        },
        {
          props: { variant: "primary" },
          style: {
            color: c["greyLightFFF"],
            backgroundColor: `var(--button-mas-primary-bg, ${c["brandDark12"]})`,
            "&:hover": {
              backgroundColor: `var(--button-mas-primary-hover-bg, ${c["brand1"]})`,
            },
            "&.Mui-disabled": {
              color: c["greyLightFFF"],
              backgroundColor: `var(--button-mas-primary-disabled-bg, ${c["brandLight64"]})`,
            },
            [buttonLoadingSelector]: {
              color: c["greyLightFFF"],
              backgroundColor: `var(--button-mas-primary-loading-bg, var(--button-mas-primary-bg, ${c["brandDark12"]}))`,
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            color: c["blackDark666"],
            backgroundColor: c["greyLightF6"],
            "&:hover": {
              backgroundColor: c["greyLightEEE"],
            },
            "&.Mui-disabled": {
              color: c["blackDarkAAA"],
              backgroundColor: c["greyLightF6"],
            },
            [buttonLoadingSelector]: {
              color: c["blackDark666"],
              backgroundColor: c["greyLightF6"],
            },
          },
        },
        {
          props: { variant: "text" },
          style: {
            ...buttonTransparentInteraction(c["brandDark12"], c["blackLightCCC"]),
            "&:hover": {
              backgroundColor: c["brandLight85"],
            },
          },
        },
        {
          props: { variant: "text-only" },
          style: {
            ...buttonTransparentInteraction(c["brandDark12"], c["blackLightCCC"]),
            padding: 0,
            minHeight: "auto",
            border: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "adornment-button" },
          style: {
            ...buttonTransparentInteraction(c["brandDark12"], c["blackLightCCC"]),
            minHeight: 32,
            maxHeight: 32,
            minWidth: 32,
            maxWidth: 32,
            borderRadius: 16,
            marginRight: "-12px",
            padding: 0,
            border: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "bordered" },
          style: {
            ...buttonTransparentInteraction(c["brandDark12"], c["blackLightCCC"]),
            border: `1px solid ${c["brandDark12"]}`,
            "&:hover": {
              backgroundColor: c["brandLight85"],
              border: `1px solid ${c["brandDark12"]}`,
            },
            "&.Mui-disabled": {
              color: c["blackLightCCC"],
              backgroundColor: "transparent",
              border: `1px solid ${c["blackLightCCC"]}`,
            },
            [buttonLoadingSelector]: {
              color: c["brandDark12"],
              backgroundColor: "transparent",
              border: `1px solid ${c["brandDark12"]}`,
            },
          },
        },
        {
          props: { variant: "auth-button" },
          style: {
            color: c["yellowWordings"],
            backgroundColor: c["yellow"],
            fontSize: "16px",
            minHeight: "45px",
            width: "100%",
            borderRadius: "32px",
            marginTop: "16px",
            marginBottom: "16px",
            ...buttonIconAndSpinnerSize("16px"),
            "&:hover": {
              backgroundColor: "#f1bf0c",
            },
            "&.Mui-disabled": {
              color: c["yellowWordings"],
              backgroundColor: c["yellowHover"],
            },
            [buttonLoadingSelector]: {
              color: c["yellowWordings"],
              backgroundColor: c["yellow"],
            },
          },
        },
      ],
    },
    MuiLoadingButton: {
      styleOverrides: {
        loadingIndicatorStart: {
          left: "12px",
        },
        loadingIndicatorEnd: {
          right: "12px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        filled: {
          color: c["greyLightFFF"],
        },
        standardInfo: {
          border: `1px solid ${c["brand1"]}`,
        },
        standardSuccess: {
          border: `1px solid ${c["successLight"]}`,
        },
        standardWarning: {
          border: `1px solid ${c["warningLight"]}`,
        },
        standardError: {
          border: `1px solid ${c["dangerLight"]}`,
        },
        icon: {
          color: c["greyLightFFF"],
        }
      },
    }
  },
});
